import { defineComponent as _defineComponent } from 'vue'
import {
  FilterKeyValueType,
  IFilter,
  IOption,
} from "@/domain/entities/filter/filter.types";
import {
  GetOptions,
  HttpBaseFilter,
  OptionMapper,
} from "@/repositories/lineInfo/lineInfoRepository.types";
import { useLineStore } from "@/stores/lineStore";
import { useBbsStore } from "@/stores/bbsStore";
import {
  PROVIDE_KEY_REMOVE_LOADING_IMAGE,
  PROVIDE_KEY_SHOW_ERROR_MODAL,
  PROVIDE_KEY_SHOW_LOADING_IMAGE,
  PROVIDE_KEY_SHOW_TOAST_MESSAGE,
  PROVIDE_KEY_UPDATE_VOC_DETAIL,
  VocSubTypeCode,
  RemoveLoadingImage,
  ShowErrorModal,
  ShowLoadingImage,
  ShowToastMessage,
  UpdateVocDetail,
  CustomCodeGroup,
  SERVICE_UID,
} from "@/types";
import { PropType, computed, inject, onMounted, ref } from "vue";

import TextInput from "@/components/shared/inputs/TextInput.vue";
import FooterButton from "@/components/shared/button/FooterButton.vue";
import TargetCalendarInput from "@/components/shared/inputs/TargetCalendarInput.vue";
import dayjs from "dayjs";
import router from "@/router";

import LostItemGuideModal from "@/components/shared/modal/LostItemGuideModal.vue";
import { IVocDetail } from "@/domain/entities/bbs";
import { IFile } from "@/domain/entities/bbs/qnaDetail.types";
import VocEditGuideMessage from "./VocEditGuideMessage.vue";

import LabeledCustomSelect from "@/components/shared/selects/LabeledCustomSelect.vue";
import VocFileUploader from "./VocFileUploader.vue";
import { useRoute } from "vue-router";
import BaseTextarea from "@/components/shared/inputs/base/BaseTextarea.vue";
import ScrollTopControl from "@/components/shared/ScrollTopControl.vue";
import CheckboxInput from "@/components/shared/inputs/CheckboxInput.vue";


const __default__ = {
  name: "VocEdit",
};

export default /*#__PURE__*/_defineComponent({
  ...__default__,
  props: {
  createMode: {
    type: Boolean,
    default: false,
  },
  editMode: {
    type: Boolean,
    default: false,
  },
  vocDetail: {
    type: Object as PropType<IVocDetail>,
    // required: true,
  },
},
  emits: ["onCompleteEdit"],
  setup(__props, { expose: __expose, emit: __emit }) {
  __expose();

const props = __props;

const emit = __emit;

const showErrorModal = inject(PROVIDE_KEY_SHOW_ERROR_MODAL) as ShowErrorModal;
const showToastMessage = inject(
  PROVIDE_KEY_SHOW_TOAST_MESSAGE
) as ShowToastMessage;
const showLoadingImage = inject(
  PROVIDE_KEY_SHOW_LOADING_IMAGE
) as ShowLoadingImage;
const removeLoadingImage = inject(
  PROVIDE_KEY_REMOVE_LOADING_IMAGE
) as RemoveLoadingImage;
const updateVocDetail = inject(
  PROVIDE_KEY_UPDATE_VOC_DETAIL
) as UpdateVocDetail;

const route = useRoute();
const vocUid = route.params.uid?.toString() ?? "";

const lineStore = useLineStore();
const bbsStore = useBbsStore();

const FieldKey = {
  vocTypeCode: "vocTypeCode",
  title: "title",
  bodyText: "bodyText",
  allocUid: "allocUid",
  lineName: "lineName",
  boardDtm: "boardDtm",
  secretYn: "secretYn",
  files: "files",
  deletedFiles: "deletedFiles",
} as const;

const scrollContainerRef = ref<HTMLElement>();

const footerBtnTitle = computed(() => (props.createMode ? "신청하기" : "수정"));
const isValidRequiredFields = computed(
  () =>
    !!props.vocDetail?.[FieldKey.vocTypeCode] &&
    !!props.vocDetail?.[FieldKey.title] &&
    !!props.vocDetail?.[FieldKey.bodyText]
);

const getFooterButtonAriaLabel = () => {
  if (isValidRequiredFields.value) {
    return footerBtnTitle.value;
  }
  return `${footerBtnTitle.value} - 필수 입력사항을 모두 작성해주세요`;
};

// filter 정의
const vocTypeFilter = ref<IFilter>();
const boardedLineFilter = ref<IFilter>();

const isLostCategorySelected = computed(() => {
  if (Array.isArray(vocTypeFilter.value?.selectedOptions)) {
    return vocTypeFilter.value?.selectedOptions.some(
      (option) => option.optionMetadata?.code === VocSubTypeCode.LOST
    );
  }
  return (
    vocTypeFilter.value?.selectedOptions?.optionMetadata?.code ===
    VocSubTypeCode.LOST
  );
});

const customCodeFilterMapper: OptionMapper<HttpBaseFilter> = (data) => {
  return {
    key: data.uid,
    name: data.name,
    optionMetadata: {
      code: data.code,
    },
  };
};

const boardedLineFilterMapper: OptionMapper<HttpBaseFilter> = (data) => {
  const { allocUid, lineRouteName, lineRouteNo, realOnDtm } = data;
  const lineName =
    lineRouteName === lineRouteNo
      ? lineRouteName
      : `${lineRouteNo}(${lineRouteName})`;

  const realOnDateTime = dayjs(realOnDtm).format("YYYY-MM-DD HH:mm");

  return {
    key: allocUid,
    name: `${lineName} (${realOnDateTime})`,
    displayText: lineName,
    optionMetadata: {
      [FieldKey.boardDtm]: realOnDateTime,
    },
  };
};

const vocSubTypeCodeGroup = CustomCodeGroup.VOQ;

const getSubTypeOptions: GetOptions<HttpBaseFilter> = (response) => {
  const targetCodeGroup = response.data?.find(
    (codeGroup) => codeGroup.codeGroup === vocSubTypeCodeGroup
  );
  return targetCodeGroup?.customCodes ?? [];
};

const getFilter = async (
  pathParam: string,
  key: string,
  filterMapper: OptionMapper<HttpBaseFilter>,
  isPublic: boolean,
  multiple = false,
  label?: string,
  dummyOptionName?: string,
  defaultOptionCode?: string,
  getOptions?: GetOptions<any>,
  allowDirectInput = false
) => {
  const result = await lineStore.getFilter(
    pathParam,
    key,
    FilterKeyValueType.FilterKey_OptionKey,
    filterMapper,
    isPublic,
    multiple,
    label,
    dummyOptionName,
    defaultOptionCode,
    undefined,
    undefined,
    undefined,
    getOptions,
    allowDirectInput
  );
  if (!result.isSuccess) {
    showToastMessage(`${label} 필터 목록을 가져오지 못했습니다`);
    return;
  }
  return result.data;
};

const updateBoardedLineOptions = (filter?: IFilter) => {
  if (!filter) return;

  // 직접 입력한 노선인지 여부
  const isDirectInputLine =
    !!props.vocDetail?.lineName?.length &&
    filter?.availableOptions.every((option) => !option.default);

  if (!isDirectInputLine) return;

  toggleShouldShowLineDirectInput(isDirectInputLine);

  filter.availableOptions = filter.availableOptions.map((option) => {
    if (option.isDirectInput) {
      option.default = isDirectInputLine;
    }
    return option;
  });
};

const getFilters = async () => {
  const today = dayjs().format("YYYY-MM-DD");
  const oneMonthAgo = dayjs().subtract(1, "month").format("YYYY-MM-DD");

  const [vocTypeResult, boardedLineResult] = await Promise.all([
    getFilter(
      `/system/custom-code?codeGroup=${vocSubTypeCodeGroup}`,
      FieldKey.vocTypeCode,
      customCodeFilterMapper,
      false,
      false,
      "문의유형",
      undefined,
      props.vocDetail?.vocTypeCode,
      getSubTypeOptions
    ),
    getFilter(
      `/service/${SERVICE_UID}/board?boardDateFrom=${oneMonthAgo}&boardDateTo=${today}`,
      FieldKey.allocUid,
      boardedLineFilterMapper,
      false,
      false,
      "노선",
      undefined,
      props.vocDetail?.allocUid,
      undefined,
      true
    ),
  ]);

  vocTypeFilter.value = vocTypeResult;
  boardedLineFilter.value = boardedLineResult;
  updateBoardedLineOptions(boardedLineFilter.value);
};

const shouldShowLineDirectInput = ref(false);
const toggleShouldShowLineDirectInput = (show: boolean) => {
  shouldShowLineDirectInput.value = show;
};

const handleBoardedLine = (option: IOption) => {
  toggleShouldShowLineDirectInput(!option.key);
  if (option.key) {
    // 직접입력한 노선명 제거
    updateBoardedLineName("");
  }
  changeDate(option.optionMetadata?.[FieldKey.boardDtm]);
};

const updateBoardedLineName = (name: string) => {
  updateVocDetail(FieldKey.lineName, name);
};

const updateSelectedOptions = (filter: IFilter, option: IOption) => {
  filter.selectedOptions = option;
};

const onSelectOption = (filter: IFilter, option: IOption) => {
  if (!filter.key) return;

  updateSelectedOptions(filter, option);

  if (filter.key === FieldKey.allocUid) {
    handleBoardedLine(option);
  }

  updateVocDetail(filter.key as keyof IVocDetail, option.key);
};

const onUpdateTitle = (title: string) => {
  updateVocDetail(FieldKey.title, title);
};

const changeDate = async (date: string) => {
  updateVocDetail(FieldKey.boardDtm, date);
};

const onUpdateBodyText = (bodyText: string) => {
  updateVocDetail(FieldKey.bodyText, bodyText);
};

// 파일 첨부 및 삭제
const inputFiles = ref<File[][]>([[], []]);

const addInputFiles = (addedFiles: File[], fileInputUid: string) => {
  const index = +fileInputUid;
  const files = props.vocDetail?.files ? [...props.vocDetail.files] : [];
  addedFiles.forEach((file) => {
    files[index] = { fileName: file.name, fileUid: "" } as IFile;
    inputFiles.value[index].push(file);
  });

  updateVocDetail(FieldKey.files, files);
};

const deleteFileInfo = (fileInfo: IFile, fileInputUid: string) => {
  const index = +fileInputUid;
  // 첨부파일 바이너리 삭제

  inputFiles.value[index] = inputFiles.value[index].filter(
    (file) => file.name !== fileInfo.fileName
  );

  // QnaDetail files 업데이트
  const fileInfos = props.vocDetail?.files?.map((file) => {
    const isTarget = file?.fileUid
      ? file.fileUid !== fileInfo.fileUid
      : file?.fileName !== fileInfo.fileName;

    return isTarget ? file : null;
  });

  updateVocDetail(FieldKey.files, fileInfos ?? []);

  // QnaDetail deletedFiles 업데이트
  if (!fileInfo.fileUid) return;
  updateDeletedFiles(fileInfo.fileUid);
};

const updateDeletedFiles = (fileUid: string) => {
  const deletedFiles = props.vocDetail?.deletedFiles
    ? [...props.vocDetail.deletedFiles]
    : [];
  deletedFiles.push({ fileUid });
  updateVocDetail(FieldKey.deletedFiles, deletedFiles);
};

const convertFilesToFormData = (inputFiles: File[][]) => {
  const files: File[] = [];

  inputFiles.forEach((inputFile) => {
    if (inputFile.length) {
      files.push(...inputFile);
    }
  });

  const formData: FormData = new FormData();
  files.forEach((file) => {
    if (file) {
      formData.append("images", file);
    }
  });

  return formData;
};

const toggleSecret = (secret: boolean) => {
  updateVocDetail(FieldKey.secretYn, secret);
};

const handleError = (title: string, result: any) => {
  showErrorModal(
    title,
    result.errorMessage,
    result.resultCode,
    result.errorTime
  );
};

const submitPost = () => {
  if (!props.vocDetail) return;

  if (props.createMode) {
    createVocDetail(props.vocDetail);
  } else {
    requestUpdateVocDetail(props.vocDetail);
  }
};

const getFilteredVocDetail = (vocDetail: IVocDetail) => {
  return {
    ...vocDetail,
    secretYn: !!vocDetail.secretYn,
    files: vocDetail.files?.filter((file) => file),
  };
};

const createVocDetail = async (vocDetail: IVocDetail) => {
  showLoadingImage();

  const formData = convertFilesToFormData(inputFiles.value);
  const createResult = await bbsStore.createVocDetail(
    getFilteredVocDetail(vocDetail),
    formData
  );

  removeLoadingImage();

  if (createResult.isSuccess) {
    showToastMessage("문의 접수가 완료되었습니다");
    goToBack();
  } else {
    handleError("문의하기 등록 오류", createResult);
  }
};

const requestUpdateVocDetail = async (vocDetail: IVocDetail) => {
  showLoadingImage();

  const formData = convertFilesToFormData(inputFiles.value);
  const updateResult = await bbsStore.updateVocDetail(
    getFilteredVocDetail(vocDetail),
    formData
  );

  removeLoadingImage();

  if (updateResult.isSuccess) {
    showToastMessage("문의 접수가 완료되었습니다");
    emit("onCompleteEdit");
  } else {
    handleError("문의하기 수정 오류", updateResult);
  }
};

/** 분실물 팝업 */
const shouldShowLostItemGuide = ref(false);

const toggleShowLostItemGuide = (show: boolean) => {
  shouldShowLostItemGuide.value = show;
};

const goToBack = () => {
  router.currentRoute.value.meta.willGoBack = true;
  router.back();
};

onMounted(async () => {
  getFilters();
});

const __returned__ = { props, emit, showErrorModal, showToastMessage, showLoadingImage, removeLoadingImage, updateVocDetail, route, vocUid, lineStore, bbsStore, FieldKey, scrollContainerRef, footerBtnTitle, isValidRequiredFields, getFooterButtonAriaLabel, vocTypeFilter, boardedLineFilter, isLostCategorySelected, customCodeFilterMapper, boardedLineFilterMapper, vocSubTypeCodeGroup, getSubTypeOptions, getFilter, updateBoardedLineOptions, getFilters, shouldShowLineDirectInput, toggleShouldShowLineDirectInput, handleBoardedLine, updateBoardedLineName, updateSelectedOptions, onSelectOption, onUpdateTitle, changeDate, onUpdateBodyText, inputFiles, addInputFiles, deleteFileInfo, updateDeletedFiles, convertFilesToFormData, toggleSecret, handleError, submitPost, getFilteredVocDetail, createVocDetail, requestUpdateVocDetail, shouldShowLostItemGuide, toggleShowLostItemGuide, goToBack, TextInput, FooterButton, TargetCalendarInput, LostItemGuideModal, VocEditGuideMessage, LabeledCustomSelect, VocFileUploader, BaseTextarea, ScrollTopControl, CheckboxInput }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})